import React from "react";
import PropTypes from "prop-types";
import { CModal, CModalBody, CModalHeader, CModalTitle, CButton } from "@coreui/react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import "./PatientDetailsModal.css";

const PatientDetailsModal = ({ isOpen, onClose, patient }) => {
  const { t } = useTranslation();

  if (!patient) return null;

  const formatDate = (dateString) => {
    if (!dateString) return t("notAvailable");
    try {
      return format(new Date(dateString), "dd/MM/yyyy");
    } catch (error) {
      return t("notAvailable");
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return t("notAvailable");
    try {
      const romeTimeFormatter = new Intl.DateTimeFormat(
        t("volte") === "volte" ? "it-IT" : "en-GB",
        {
          timeZone: "Europe/Rome",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: t("volte") !== "volte",
        }
      );
      const offset = new Date().getTimezoneOffset() * 60000;
      const romeTime = new Date(new Date(dateTimeString).getTime() - offset);
      return romeTimeFormatter.format(new Date(romeTime));
    } catch (error) {
      return t("notAvailable");
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? "#28a745" : "#dc3545";
  };

  const getStatusText = (isActive) => {
    return isActive ? t("active") : t("inactive");
  };

  // Parse multiple doctors information
  const getAssignedDoctorsInfo = () => {
    if (!patient.allAssignedDoctorNames) {
      return [];
    }


    const names = patient.allAssignedDoctorNames.split(", ");
    const emails = patient.allAssignedDoctorEmails ? patient.allAssignedDoctorEmails.split(", ") : [];
    const ids = patient.allAssignedDoctorIds ? patient.allAssignedDoctorIds.split(", ") : [];

    return names.map((name, index) => ({
      name: name.trim(),
      email: emails[index] ? emails[index].trim() : t("notAvailable"),
      id: ids[index] ? ids[index].trim() : t("notAvailable"),
    }));
  };

  const assignedDoctors = getAssignedDoctorsInfo();

  return (
    <CModal
      visible={isOpen}
      onClose={onClose}
      size="lg"
      className="patient-details-modal"
    >
      <CModalHeader className="patient-details-header">
        <CModalTitle className="patient-details-title">
          {t("patientDetails")}
        </CModalTitle>
      </CModalHeader>
      <CModalBody className="patient-details-body">
        <div className="patient-details-container">
          {/* Personal Information Section */}
          <div className="details-section">
            <h5 className="section-title">{t("personalInformation")}</h5>
            <div className="details-grid">
              <div className="detail-item">
                <label className="detail-label">{t("name")}:</label>
                <span className="detail-value">{patient.name || t("notAvailable")}</span>
              </div>
              <div className="detail-item">
                <label className="detail-label">{t("surname")}:</label>
                <span className="detail-value">{patient.surname || t("notAvailable")}</span>
              </div>
              <div className="detail-item">
                <label className="detail-label">{t("fiscalCode")}:</label>
                <span className="detail-value">{patient.fiscalCode || t("notAvailable")}</span>
              </div>
              <div className="detail-item">
                <label className="detail-label">{t("sex")}:</label>
                <span className="detail-value">{patient.sex || t("notAvailable")}</span>
              </div>
              <div className="detail-item">
                <label className="detail-label">{t("birthDate")}:</label>
                <span className="detail-value">{formatDate(patient.birthDate)}</span>
              </div>
              <div className="detail-item">
                <label className="detail-label">{t("email")}:</label>
                <span className="detail-value">{patient.email || t("notAvailable")}</span>
              </div>
            </div>
          </div>

          {/* Physical Information Section */}
          <div className="details-section">
            <h5 className="section-title">{t("physicalInformation")}</h5>
            <div className="details-grid">
              <div className="detail-item">
                <label className="detail-label">{t("weight")} (kg):</label>
                <span className="detail-value">
                  {patient.weight ? `${patient.weight}` : t("notAvailable")}
                </span>
              </div>
              <div className="detail-item">
                <label className="detail-label">{t("height")} (cm):</label>
                <span className="detail-value">
                  {patient.height ? `${patient.height}` : t("notAvailable")}
                </span>
              </div>
              <div className="detail-item">
                <label className="detail-label">{t("language")}:</label>
                <span className="detail-value">{patient.language || t("notAvailable")}</span>
              </div>
            </div>
          </div>

          {/* Account Information Section */}
          <div className="details-section">
            <h5 className="section-title">{t("accountInformation")}</h5>
            <div className="details-grid">
              <div className="detail-item">
                <label className="detail-label">{t("activeStatus")}:</label>
                <span 
                  className="detail-value status-badge"
                  style={{ 
                    color: getStatusColor(patient.active),
                    fontWeight: "bold"
                  }}
                >
                  {getStatusText(patient.active)}
                </span>
              </div>
              <div className="detail-item">
                <label className="detail-label">{t("registrationDate")}:</label>
                <span className="detail-value">{formatDateTime(patient.registrationDate)}</span>
              </div>
            </div>
          </div>

          {/* Assigned Doctors Section */}
          <div className="details-section">
            <h5 className="section-title">
              {t("assignedDoctorsInformation")} 
              {assignedDoctors.length > 0 && (
                <span className="doctor-count">({assignedDoctors.length})</span>
              )}
            </h5>
            
            {assignedDoctors.length === 0 ? (
              <div className="no-doctors-assigned">
                <span className="detail-value" style={{ fontStyle: "italic", color: "#6c757d" }}>
                  {t("notAssigned")}
                </span>
              </div>
            ) : (
              <div className="doctors-list">
                {assignedDoctors.map((doctor, index) => (
                  <div key={index} className="doctor-item">
                    <div className="doctor-header">
                      <h6 className="doctor-title">
                        {t("doctor")} {index + 1}
                        {index === 0 && assignedDoctors.length > 1 && (
                          <span className="primary-badge">{t("primary")}</span>
                        )}
                      </h6>
                    </div>
                    <div className="doctor-details">
                      <div className="detail-item">
                        <label className="detail-label">{t("doctorId")}:</label>
                        <span className="detail-value">{doctor.id}</span>
                      </div>
                      <div className="detail-item">
                        <label className="detail-label">{t("name")}:</label>
                        <span className="detail-value">{doctor.name}</span>
                      </div>
                      <div className="detail-item">
                        <label className="detail-label">{t("email")}:</label>
                        <span className="detail-value">{doctor.email}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <CButton
              color="secondary"
              onClick={onClose}
              className="close-button"
            >
              {t("close")}
            </CButton>
          </div>
        </div>
      </CModalBody>
    </CModal>
  );
};

PatientDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patient: PropTypes.object,
};

export default PatientDetailsModal;